import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

// Eager load only the homepage and critical components
import Index from "./pages/Index";
import { RequireTermsAcceptance } from "@/components/RequireTermsAcceptance";
import ProtectedAdminRoute from "@/components/admin/ProtectedAdminRoute";
import ProtectedOrganizerRoute from "@/components/organizer/ProtectedOrganizerRoute";
import ProtectedEmployerRoute from "@/components/employer/ProtectedEmployerRoute";
import ProtectedJobSeekerRoute from "@/components/jobseeker/ProtectedJobSeekerRoute";
import FlashNewsBar from "@/components/FlashNewsBar";
import CookieConsent from "@/components/CookieConsent";
import { HelpWidget } from "@/components/HelpWidget";
import { BetaBanner } from "@/components/BetaBanner";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import { ScrollToTop } from "@/components/ScrollToTop";
import { WhatsNewModal } from "@/components/WhatsNewModal";
import LivePriceStrip from "@/components/LivePriceStrip";

// Lazy load all other routes for better performance
const News = lazy(() => import("./pages/News"));
const SentimentBoard = lazy(() => import("./pages/SentimentBoard"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Learn = lazy(() => import("./pages/Learn"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const PostJob = lazy(() => import("./pages/PostJob"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Profile = lazy(() => import("./pages/Profile"));
const Auth = lazy(() => import("./pages/Auth"));
const Tools = lazy(() => import("./pages/Tools"));
const Market = lazy(() => import("./pages/Market"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Settings = lazy(() => import("./pages/Settings"));

const LearnArticle = lazy(() => import("./pages/LearnArticle"));
const Newsletter = lazy(() => import("./pages/Newsletter"));
const SavedArticles = lazy(() => import("./pages/SavedArticles"));
const SavedJobs = lazy(() => import("./pages/SavedJobs"));
const MyApplications = lazy(() => import("./pages/MyApplications"));
const EventsCalendar = lazy(() => import("./pages/EventsCalendar"));
const EventsPricing = lazy(() => import("./pages/EventsPricing"));
const PredictMarket = lazy(() => import("./pages/PredictMarket"));
const InfluencerLeaderboard = lazy(() => import("./pages/InfluencerLeaderboard"));
const BitcoinTreasuries = lazy(() => import("./pages/BitcoinTreasuries"));
const TopHolders = lazy(() => import("./pages/TopHolders"));
const OnChainMetrics = lazy(() => import("./pages/OnChainMetrics"));
const LightningNetwork = lazy(() => import("./pages/LightningNetwork"));
const DCACalculator = lazy(() => import("./pages/DCACalculator"));
const MiningCalculator = lazy(() => import("./pages/MiningCalculator"));
const MiningDashboard = lazy(() => import("./pages/MiningDashboard"));
const AboutBitcoin = lazy(() => import("./pages/AboutBitcoin"));
const WalletSelector = lazy(() => import("./pages/WalletSelector"));
const BitcoinLenders = lazy(() => import("./pages/BitcoinLenders"));
const BitcoinCards = lazy(() => import("./pages/BitcoinCards"));
const RequestRole = lazy(() => import("./pages/RequestRole"));
const Advertise = lazy(() => import("./pages/Advertise"));
const WhaleAlertsPreview = lazy(() => import("./pages/WhaleAlertsPreview"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Security = lazy(() => import("./pages/Security"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BitcoinPriceAnalysis = lazy(() => import("./pages/BitcoinPriceAnalysis"));
const BitcoinIQ = lazy(() => import("./pages/BitcoinIQ"));
const BitcoinIQLeaderboard = lazy(() => import("./pages/BitcoinIQLeaderboard"));

// Admin routes - lazy loaded
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminEvents = lazy(() => import("./pages/admin/Events"));
const AdminUsers = lazy(() => import("./pages/admin/Users"));
const AdminBanners = lazy(() => import("./pages/admin/Banners"));
const AdminJobPostings = lazy(() => import("./pages/admin/JobPostings"));
const AdminEventSubmissions = lazy(() => import("./pages/admin/EventSubmissions"));
const AdminContactMessages = lazy(() => import("./pages/admin/ContactMessages"));
const AdminFlashAnnouncements = lazy(() => import("./pages/admin/FlashAnnouncements"));
const AdminSystemAnnouncements = lazy(() => import("./pages/admin/SystemAnnouncements"));
const AdminEmailCampaigns = lazy(() => import("./pages/admin/EmailCampaigns"));
const AdminNewsMonitoring = lazy(() => import("./pages/admin/NewsMonitoring"));
const AdminCronJobs = lazy(() => import("./pages/admin/CronJobs"));
const AdminNewsletterAnalytics = lazy(() => import("./pages/admin/NewsletterAnalytics"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/EmailTemplates"));
const AdminWhatsNewSettings = lazy(() => import("./pages/admin/WhatsNewSettings"));
const AdminRoleRequests = lazy(() => import("./pages/admin/RoleRequests"));
const AdminPredictions = lazy(() => import("./pages/admin/Predictions"));
const AdminEventPricingSettings = lazy(() => import("./pages/admin/EventPricingSettings"));
const AdminBetaDiscountSettings = lazy(() => import("./pages/admin/BetaDiscountSettings"));
const AdminBannerAnalytics = lazy(() => import("./pages/admin/BannerAnalytics"));
const AdminTreasuries = lazy(() => import("./pages/admin/Treasuries"));
const AdminLenders = lazy(() => import("./pages/admin/Lenders"));
const AdminCards = lazy(() => import("./pages/admin/Cards"));
const AdminBooks = lazy(() => import("./pages/admin/Books"));
const AdminPodcasts = lazy(() => import("./pages/admin/Podcasts"));
const AdminTwitterAccounts = lazy(() => import("./pages/admin/TwitterAccounts"));
const AdminSiteSettings = lazy(() => import("./pages/admin/SiteSettings"));
const AdminRefundRequests = lazy(() => import("./pages/admin/RefundRequests"));
const AdminNewsletterSponsors = lazy(() => import("./pages/admin/NewsletterSponsors"));
const AdminGeneratePromotionalBanners = lazy(() => import("./pages/admin/GeneratePromotionalBanners"));
const AdminGenerateSocialMediaGraphics = lazy(() => import("./pages/admin/GenerateSocialMediaGraphics"));
const AdminMediaLibrary = lazy(() => import("./pages/admin/MediaLibrary"));
const AdminExternalJobs = lazy(() => import("./pages/admin/ExternalJobs"));
const AdminSecurityStatus = lazy(() => import("./pages/admin/SecurityStatus"));
const AdminProductionMonitor = lazy(() => import("./pages/admin/ProductionMonitor"));
const AdminLearningCourses = lazy(() => import("./pages/admin/LearningCourses"));
const AdminCourseContent = lazy(() => import("./pages/admin/CourseContent"));

// Learning Academy routes - lazy loaded
const Courses = lazy(() => import("./pages/Courses"));
const CourseDetail = lazy(() => import("./pages/CourseDetail"));
const CourseLearning = lazy(() => import("./pages/CourseLearning"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const SampleCertificate = lazy(() => import("./pages/SampleCertificate"));
const QuizView = lazy(() => import("./pages/QuizView"));

// Organizer routes - lazy loaded
const OrganizerLayout = lazy(() => import("./components/organizer/OrganizerLayout"));
const OrganizerDashboard = lazy(() => import("./pages/organizer/Dashboard"));
const OrganizerEvents = lazy(() => import("./pages/organizer/Events"));
const OrganizerAnalytics = lazy(() => import("./pages/organizer/Analytics"));
const OrganizerPricing = lazy(() => import("./pages/organizer/Pricing"));

// Employer routes - lazy loaded
const EmployerLayout = lazy(() => import("./components/employer/EmployerLayout"));
const EmployerDashboard = lazy(() => import("./pages/employer/Dashboard"));
const EmployerApplications = lazy(() => import("./pages/employer/Applications"));
const EmployerAnalytics = lazy(() => import("./pages/employer/Analytics"));

// Job Seeker routes - lazy loaded
const JobSeekerLayout = lazy(() => import("./components/jobseeker/JobSeekerLayout"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      gcTime: 300000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <Toaster />
          <SonnerToaster />
          <BrowserRouter>
            <div className="relative">
              <BetaBanner />
              <FlashNewsBar />
              <LivePriceStrip />
            </div>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/news" element={<Suspense fallback={<PageLoader />}><News /></Suspense>} />
              <Route path="/sentiment" element={<Suspense fallback={<PageLoader />}><SentimentBoard /></Suspense>} />
              <Route path="/news/:id" element={<Suspense fallback={<PageLoader />}><ArticleDetail /></Suspense>} />
              <Route path="/about" element={<Suspense fallback={<PageLoader />}><About /></Suspense>} />
              <Route path="/contact" element={<Suspense fallback={<PageLoader />}><Contact /></Suspense>} />
              <Route path="/learn" element={<Suspense fallback={<PageLoader />}><Learn /></Suspense>} />
              <Route path="/learn/:slug" element={<Suspense fallback={<PageLoader />}><LearnArticle /></Suspense>} />
              <Route path="/jobs" element={<Suspense fallback={<PageLoader />}><Jobs /></Suspense>} />
              <Route path="/jobs/post" element={<Suspense fallback={<PageLoader />}><PostJob /></Suspense>} />
              <Route path="/jobs/my-applications" element={<Suspense fallback={<PageLoader />}><MyApplications /></Suspense>} />
              <Route path="/jobs/:id" element={<Suspense fallback={<PageLoader />}><JobDetail /></Suspense>} />
              <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
              <Route path="/tools" element={<Suspense fallback={<PageLoader />}><Tools /></Suspense>} />
              <Route path="/market" element={<Suspense fallback={<PageLoader />}><Market /></Suspense>} />
              <Route path="/events" element={<Suspense fallback={<PageLoader />}><Events /></Suspense>} />
              <Route path="/events/calendar" element={<Suspense fallback={<PageLoader />}><EventsCalendar /></Suspense>} />
              <Route path="/events/pricing" element={<Suspense fallback={<PageLoader />}><EventsPricing /></Suspense>} />
              <Route path="/events/:slug" element={<Suspense fallback={<PageLoader />}><EventDetail /></Suspense>} />
              <Route path="/calendar" element={<Suspense fallback={<PageLoader />}><EventsCalendar /></Suspense>} />
              <Route path="/events-pricing" element={<Suspense fallback={<PageLoader />}><EventsPricing /></Suspense>} />
              <Route path="/predict" element={<Suspense fallback={<PageLoader />}><PredictMarket /></Suspense>} />
              <Route path="/influencers" element={<Suspense fallback={<PageLoader />}><InfluencerLeaderboard /></Suspense>} />
              <Route path="/treasuries" element={<Suspense fallback={<PageLoader />}><BitcoinTreasuries /></Suspense>} />
              <Route path="/top-holders" element={<Suspense fallback={<PageLoader />}><TopHolders /></Suspense>} />
              <Route path="/onchain-metrics" element={<Suspense fallback={<PageLoader />}><OnChainMetrics /></Suspense>} />
              <Route path="/lightning" element={<Suspense fallback={<PageLoader />}><LightningNetwork /></Suspense>} />
              <Route path="/dca-calculator" element={<Suspense fallback={<PageLoader />}><DCACalculator /></Suspense>} />
              <Route path="/mining-calculator" element={<Suspense fallback={<PageLoader />}><MiningCalculator /></Suspense>} />
              <Route path="/mining-dashboard" element={<Suspense fallback={<PageLoader />}><MiningDashboard /></Suspense>} />
              <Route path="/about-bitcoin" element={<Suspense fallback={<PageLoader />}><AboutBitcoin /></Suspense>} />
              <Route path="/wallet-selector" element={<Suspense fallback={<PageLoader />}><WalletSelector /></Suspense>} />
              <Route path="/bitcoin-lenders" element={<Suspense fallback={<PageLoader />}><BitcoinLenders /></Suspense>} />
              <Route path="/bitcoin-cards" element={<Suspense fallback={<PageLoader />}><BitcoinCards /></Suspense>} />
              <Route path="/advertise" element={<Suspense fallback={<PageLoader />}><Advertise /></Suspense>} />
              <Route path="/whale-alerts" element={<Suspense fallback={<PageLoader />}><WhaleAlertsPreview /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={<PageLoader />}><TermsConditions /></Suspense>} />
              <Route path="/refund-policy" element={<Suspense fallback={<PageLoader />}><RefundPolicy /></Suspense>} />
              <Route path="/terms-of-use" element={<Suspense fallback={<PageLoader />}><TermsOfUse /></Suspense>} />
              <Route path="/sitemap" element={<Suspense fallback={<PageLoader />}><Sitemap /></Suspense>} />
              <Route path="/faq" element={<Suspense fallback={<PageLoader />}><FAQ /></Suspense>} />
              <Route path="/security" element={<Suspense fallback={<PageLoader />}><Security /></Suspense>} />
              <Route path="/price-analysis" element={<Suspense fallback={<PageLoader />}><BitcoinPriceAnalysis /></Suspense>} />
              <Route path="/bitcoin-iq" element={<Suspense fallback={<PageLoader />}><BitcoinIQ /></Suspense>} />
              <Route path="/bitcoin-iq/leaderboard" element={<Suspense fallback={<PageLoader />}><BitcoinIQLeaderboard /></Suspense>} />
              <Route path="/courses" element={<Suspense fallback={<PageLoader />}><Courses /></Suspense>} />
              <Route path="/courses/:slug" element={<Suspense fallback={<PageLoader />}><CourseDetail /></Suspense>} />
              <Route path="/courses/:slug/learn" element={<Suspense fallback={<PageLoader />}><CourseLearning /></Suspense>} />
              <Route path="/quiz/:quizId" element={<Suspense fallback={<PageLoader />}><QuizView /></Suspense>} />
              <Route path="/verify/:certificateNumber" element={<Suspense fallback={<PageLoader />}><VerifyCertificate /></Suspense>} />
              <Route path="/sample-certificate" element={<Suspense fallback={<PageLoader />}><SampleCertificate /></Suspense>} />

              {/* Protected Routes - Require Terms Acceptance */}
              <Route element={<RequireTermsAcceptance><Outlet /></RequireTermsAcceptance>}>
                <Route path="/profile" element={<Suspense fallback={<PageLoader />}><Profile /></Suspense>} />
                <Route path="/settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
                <Route path="/portfolio" element={<Suspense fallback={<PageLoader />}><Portfolio /></Suspense>} />
                
                <Route path="/newsletter" element={<Suspense fallback={<PageLoader />}><Newsletter /></Suspense>} />
                <Route path="/saved-articles" element={<Suspense fallback={<PageLoader />}><SavedArticles /></Suspense>} />
                <Route path="/saved-jobs" element={<Suspense fallback={<PageLoader />}><SavedJobs /></Suspense>} />
                <Route path="/request-role" element={<Suspense fallback={<PageLoader />}><RequestRole /></Suspense>} />

                {/* Job Seeker Routes */}
                <Route element={<ProtectedJobSeekerRoute><Outlet /></ProtectedJobSeekerRoute>}>
                  <Route path="/job-seeker" element={<Suspense fallback={<PageLoader />}><JobSeekerLayout /></Suspense>}>
                    <Route index element={<Suspense fallback={<PageLoader />}><MyApplications /></Suspense>} />
                    <Route path="applications" element={<Suspense fallback={<PageLoader />}><MyApplications /></Suspense>} />
                    <Route path="saved" element={<Suspense fallback={<PageLoader />}><SavedJobs /></Suspense>} />
                  </Route>
                </Route>

                {/* Employer Routes */}
                <Route element={<ProtectedEmployerRoute><Outlet /></ProtectedEmployerRoute>}>
                  <Route path="/employer" element={<Suspense fallback={<PageLoader />}><EmployerLayout /></Suspense>}>
                    <Route index element={<Suspense fallback={<PageLoader />}><EmployerDashboard /></Suspense>} />
                    <Route path="applications/:jobId" element={<Suspense fallback={<PageLoader />}><EmployerApplications /></Suspense>} />
                    <Route path="analytics" element={<Suspense fallback={<PageLoader />}><EmployerAnalytics /></Suspense>} />
                    <Route path="post-job" element={<Suspense fallback={<PageLoader />}><PostJob /></Suspense>} />
                  </Route>
                </Route>

                {/* Organizer Routes */}
                <Route element={<ProtectedOrganizerRoute><Outlet /></ProtectedOrganizerRoute>}>
                  <Route path="/organizer" element={<Suspense fallback={<PageLoader />}><OrganizerLayout /></Suspense>}>
                    <Route index element={<Suspense fallback={<PageLoader />}><OrganizerDashboard /></Suspense>} />
                    <Route path="events" element={<Suspense fallback={<PageLoader />}><OrganizerEvents /></Suspense>} />
                    <Route path="analytics" element={<Suspense fallback={<PageLoader />}><OrganizerAnalytics /></Suspense>} />
                    <Route path="pricing" element={<Suspense fallback={<PageLoader />}><OrganizerPricing /></Suspense>} />
                  </Route>
                </Route>

                {/* Admin Routes */}
                <Route element={<ProtectedAdminRoute><Outlet /></ProtectedAdminRoute>}>
                  <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminLayout /></Suspense>}>
                    <Route index element={<Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense>} />
                    <Route path="events" element={<Suspense fallback={<PageLoader />}><AdminEvents /></Suspense>} />
                    <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUsers /></Suspense>} />
                    <Route path="banners" element={<Suspense fallback={<PageLoader />}><AdminBanners /></Suspense>} />
                    <Route path="media-library" element={<Suspense fallback={<PageLoader />}><AdminMediaLibrary /></Suspense>} />
                    <Route path="job-postings" element={<Suspense fallback={<PageLoader />}><AdminJobPostings /></Suspense>} />
                    <Route path="event-submissions" element={<Suspense fallback={<PageLoader />}><AdminEventSubmissions /></Suspense>} />
                    <Route path="contact-messages" element={<Suspense fallback={<PageLoader />}><AdminContactMessages /></Suspense>} />
                    <Route path="flash-announcements" element={<Suspense fallback={<PageLoader />}><AdminFlashAnnouncements /></Suspense>} />
                    <Route path="announcements" element={<Suspense fallback={<PageLoader />}><AdminSystemAnnouncements /></Suspense>} />
                    <Route path="emails" element={<Suspense fallback={<PageLoader />}><AdminEmailCampaigns /></Suspense>} />
                    <Route path="news-monitoring" element={<Suspense fallback={<PageLoader />}><AdminNewsMonitoring /></Suspense>} />
                    <Route path="cron" element={<Suspense fallback={<PageLoader />}><AdminCronJobs /></Suspense>} />
                    <Route path="newsletter-analytics" element={<Suspense fallback={<PageLoader />}><AdminNewsletterAnalytics /></Suspense>} />
                    <Route path="templates" element={<Suspense fallback={<PageLoader />}><AdminEmailTemplates /></Suspense>} />
                    <Route path="whats-new" element={<Suspense fallback={<PageLoader />}><AdminWhatsNewSettings /></Suspense>} />
                    <Route path="role-requests" element={<Suspense fallback={<PageLoader />}><AdminRoleRequests /></Suspense>} />
                    <Route path="predictions" element={<Suspense fallback={<PageLoader />}><AdminPredictions /></Suspense>} />
                    <Route path="event-pricing" element={<Suspense fallback={<PageLoader />}><AdminEventPricingSettings /></Suspense>} />
                    <Route path="beta-discount" element={<Suspense fallback={<PageLoader />}><AdminBetaDiscountSettings /></Suspense>} />
                    <Route path="analytics" element={<Suspense fallback={<PageLoader />}><AdminBannerAnalytics /></Suspense>} />
                    <Route path="treasuries" element={<Suspense fallback={<PageLoader />}><AdminTreasuries /></Suspense>} />
                    <Route path="lenders" element={<Suspense fallback={<PageLoader />}><AdminLenders /></Suspense>} />
                    <Route path="cards" element={<Suspense fallback={<PageLoader />}><AdminCards /></Suspense>} />
                    <Route path="books" element={<Suspense fallback={<PageLoader />}><AdminBooks /></Suspense>} />
                    <Route path="podcasts" element={<Suspense fallback={<PageLoader />}><AdminPodcasts /></Suspense>} />
                    <Route path="twitter-accounts" element={<Suspense fallback={<PageLoader />}><AdminTwitterAccounts /></Suspense>} />
                    <Route path="settings" element={<Suspense fallback={<PageLoader />}><AdminSiteSettings /></Suspense>} />
                    <Route path="refund-requests" element={<Suspense fallback={<PageLoader />}><AdminRefundRequests /></Suspense>} />
                    <Route path="newsletter-sponsors" element={<Suspense fallback={<PageLoader />}><AdminNewsletterSponsors /></Suspense>} />
                    <Route path="generate-banners" element={<Suspense fallback={<PageLoader />}><AdminGeneratePromotionalBanners /></Suspense>} />
                    <Route path="generate-social-media-graphics" element={<Suspense fallback={<PageLoader />}><AdminGenerateSocialMediaGraphics /></Suspense>} />
                    <Route path="external-jobs" element={<Suspense fallback={<PageLoader />}><AdminExternalJobs /></Suspense>} />
                    <Route path="security-status" element={<Suspense fallback={<PageLoader />}><AdminSecurityStatus /></Suspense>} />
                    <Route path="production-monitor" element={<Suspense fallback={<PageLoader />}><AdminProductionMonitor /></Suspense>} />
                    <Route path="learning-courses" element={<Suspense fallback={<PageLoader />}><AdminLearningCourses /></Suspense>} />
                    <Route path="course-content/:courseId" element={<Suspense fallback={<PageLoader />}><AdminCourseContent /></Suspense>} />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
            </Routes>
            
            <WhatsNewModal />
            <CookieConsent />
            <HelpWidget />
            <FeedbackWidget />
          </BrowserRouter>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
